"use client";

import { Children } from "react";
import { cx } from "cva";
import { carouselStyles, carouselContainerStyles, carouselWrapperStyles } from "./styles/carousel.styles";
import { carouselNavigationStyles } from "./styles/carousel-navigation.styles";

import type { CarouselProps } from "./types/carousel.types";
import { CarouselProgressBar } from "./carousel-progress-bar";
import { CarouselPaginationDots } from "./carousel-pagination-dots";

import { CarouselItem } from "./carousel-item";
import { CarouselNavigationButton } from "./carousel-navigation-button";
import { CarouselProvider } from "./carousel.context";
import { useCarousel } from "./use-carousel";

type CarouselComponents = {
  Item: typeof CarouselItem;
};

export const Carousel: React.FC<CarouselProps> & CarouselComponents = (props) => {
  const {
    children,
    className,
    style,
    dragFree,
    autoScroll = false,
    loop = false,
    hideArrowsOnMobile = false,
    hideControls = false,
    paginationType = "progress",
    wheelGestures = false,
    ...rest
  } = props;

  const classes = carouselStyles({ className });
  const { activeId, activeIndex, canScrollNext, canScrollPrev, emblaRef, handleNext, handlePrev, progress, scrollTo } =
    useCarousel({
      autoScroll,
      dragFree,
      loop,
      wheelGestures,
    });

  return (
    <CarouselProvider activeId={activeId} loop={loop}>
      <div
        className={classes}
        {...rest}
        style={{ "--mds-carousel-progress": progress, ...style } as React.CSSProperties}
      >
        <div className={carouselWrapperStyles()} ref={emblaRef}>
          <div className={carouselContainerStyles({ loop })}>{children}</div>

          {!hideControls && (
            <div className={cx(carouselNavigationStyles(), hideArrowsOnMobile && "hidden lg:flex")}>
              <CarouselNavigationButton action="prev" onClick={handlePrev} disabled={!canScrollPrev} />
              <CarouselNavigationButton action="next" onClick={handleNext} disabled={!canScrollNext} />
            </div>
          )}
        </div>

        {!hideControls && paginationType === "progress" ? (
          <CarouselProgressBar />
        ) : !hideControls && paginationType === "dots" ? (
          <CarouselPaginationDots activeIndex={activeIndex} total={Children.count(children)} scrollTo={scrollTo} />
        ) : null}
      </div>
    </CarouselProvider>
  );
};

Carousel.Item = CarouselItem;

Carousel.displayName = "Carousel";
